"use client";
import { clsx } from "clsx";
import { Eye, EyeOff } from "lucide-react";
import type { InputHTMLAttributes } from "react";
import { useState, forwardRef } from "react";
import { type FieldError } from "react-hook-form";

import { cn } from "../../lib/utils";
import { FormMessage, FormControl, FormItem, FormLabel } from "../ui/form";
import { Input as ShadInput } from "../ui/input";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  containerClass?: string;
  error?: FieldError;
  subText?: string;
}

/**
 *
 * NOTE: Must be used in a Form(shadcn form)
 */

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      containerClass,
      error,
      id,
      required,
      type = "text",
      subText,
      ...rest
    },
    ref
  ) => {
    const [isPasswordField, setIsPasswordField] = useState(type === "password");

    return (
      <FormItem
        className={cn(
          "space-y-0",
          { "flex flex-col gap-[2px]": !!label },
          { [`${containerClass}`]: !!containerClass }
        )}
      >
        {label ? (
          <FormLabel
            htmlFor={id}
            className={clsx("text-sm !m-[2px]", {
              "text-danger-100": !!error,
            })}
          >
            {label} {!!required && <span className="text-danger-50">*</span>}
          </FormLabel>
        ) : null}
        <FormControl className="">
          <div className="relative">
            <ShadInput
              ref={ref}
              id={id}
              type={
                isPasswordField ? "password" : type === "password" ? "text" : type
              }
              {...rest}
              className={cn(
                "!mt-0 border focus:ring-transparent focus-visible:ring-transparent transition-colors",
                {
                  "border-red-500 focus:!border-red-500": !!error,
                  "border-neutral-400 focus:!border-neutral-700 focus-visible:!border-neutral-700": !error,
                }
              )}
            />

            {type === "password" && (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
              <div
                onClick={() => {
                  setIsPasswordField((prev) => !prev);
                }}
              >
                {isPasswordField ? (
                  <Eye className="absolute w-5 h-5 right-2 top-1/2 -translate-y-1/2 transform cursor-pointer" />
                ) : (
                  <EyeOff className="absolute w-5 h-5 right-2 top-1/2 -translate-y-1/2 transform cursor-pointer" />
                )}
              </div>
            )}
          </div>
        </FormControl>
        {!!error && <FormMessage className="text-xs" />}
        {!!subText && (
          <p className="mt-0.5 text-xs font-normal leading-4 text-neutral-600">
            {subText}
          </p>
        )}
      </FormItem>
    );
  }
);

Input.displayName = "Input";

export default Input;
